import { useEffect, useRef } from 'react';

/**
 * "FECHAR SEM SALVAR?" SÓ QUANDO ALGO MUDOU (16/10/2026, pedido do Heitor: "caso não tenha feito nenhuma ação não faz
 * sentido... somente caso seja feito alguma ação"). Ele abriu o Novo Termo de Portabilidade, não mexeu em nada, deu Esc,
 * e a casca perguntou. A casca pergunta sempre que o formulário não diz se há edição (`temEdicaoNaoSalva` ausente), e
 * 149 das 158 cascas não dizem.
 *
 * Para elas, a casca agora OLHA o próprio corpo, contra o estado em que ABRIU (não contra "vazio"). O erro antigo era
 * perguntar à toa; o erro a evitar agora é o contrário, FECHAR CALADO com algo feito. Por isso a regra é assimétrica
 * (Maestro, 16/10): qualquer entrada marca "mexeu", e SÓ a comparação com a foto de um campo pode liberar. Entrada que
 * não deixa campo para comparar (estado fora de input) é mudança, e pergunta.
 *
 *  - FOTO: o valor de cada campo na abertura. O que já vem preenchido (a Data de Autorização com hoje) é ponto de
 *    partida. O que aparece depois (outra aba, linha nova) é fotografado quando a pessoa chega nele, antes de mexer.
 *  - CAMPO (input/select/textarea): input/change marca "mexeu"; libera se todos os campos batem com a foto (voltar ao
 *    original fecha direto). Campo alterado sem foto é mudança.
 *  - FORA DE CAMPO, sempre mudança:
 *      clique em algo clicável (botão, link, [role], [tabindex], cursor de mão) que não seja um campo;
 *      arquivo SOLTO em qualquer lugar do modal (a zona de drop é padrão obrigatório da casa);
 *      colar fora de um campo (a colagem de imagem das Demandas); input em contenteditable;
 *      arrastar para reordenar (dragstart/drop);
 *      tecla que não é navegação nem modificador, num elemento que não é campo (combobox/lista custom por teclado),
 *      e seta/Home/End num widget com papel (combobox, listbox, opção, slider…).
 *  - O próprio Esc não conta: é a tecla de fechar.
 * Dado que a TELA carrega depois de abrir não conta: ninguém mexeu.
 */
type Controle = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
const CAMPOS = 'input, select, textarea';
const ignorado = (el: Element) => el instanceof HTMLInputElement && ['button', 'submit', 'reset', 'hidden'].includes(el.type);
const valorDe = (el: Controle): string => {
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return el.checked ? '1' : '0';
  if (el instanceof HTMLInputElement && el.type === 'file') return String(el.files?.length ?? 0);
  if (el instanceof HTMLSelectElement && el.multiple) return Array.from(el.selectedOptions).map((o) => o.value).join('\u0000');
  return el.value;
};
const ehCampo = (el: EventTarget | null): el is Controle =>
  (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) && !ignorado(el);
/** O alvo é um campo, ou um rótulo que aponta para um (o clique no rótulo vira change no campo, que é comparado). */
const ehCampoOuRotulo = (el: Element | null): boolean => {
  if (!el) return false;
  if (ehCampo(el)) return true;
  const rotulo = el.closest('label');
  return !!rotulo && (!!rotulo.htmlFor || !!rotulo.querySelector(CAMPOS));
};
const CLICAVEL = 'button, a[href], [role], [tabindex], .cursor-pointer, summary';
const clicavel = (el: Element, corpo: HTMLElement): boolean => {
  const alvo = el.closest(CLICAVEL);
  if (alvo && corpo.contains(alvo) && !(alvo as HTMLButtonElement).disabled) return true;
  try { return typeof window !== 'undefined' && window.getComputedStyle(el).cursor === 'pointer'; } catch { return false; }
};
const NAVEGACAO = new Set(['Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'ArrowUp', 'ArrowDown', 'ArrowLeft',
  'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']);
const SETAS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);
const WIDGET = '[role="combobox"], [role="listbox"], [role="option"], [role="slider"], [role="spinbutton"], [role="menu"], [role="menuitem"], [role="grid"], [role="gridcell"], [role="tree"], [role="treeitem"], [role="radiogroup"]';

export function useMudouNoCorpo() {
  const corpoRef = useRef<HTMLDivElement | null>(null);
  const fotos = useRef(new Map<Controle, string>());
  const mexeuEmCampo = useRef(false);
  /** Mexeu em algo que não tem campo para comparar: pergunta, sempre. */
  const foraDeCampo = useRef(false);

  useEffect(() => {
    const corpo = corpoRef.current;
    if (!corpo) return;
    const dialogo = (corpo.closest('[role="dialog"]') as HTMLElement | null) || corpo;
    const fotografar = () => {
      corpo.querySelectorAll(CAMPOS).forEach((el) => {
        if (!ehCampo(el)) return;
        if (!fotos.current.has(el)) fotos.current.set(el, valorDe(el));
      });
    };
    fotografar(); // o estado em que abriu
    const aoMexer = (e: Event) => {
      const t = e.target as Element | null;
      if (ehCampo(t)) {
        mexeuEmCampo.current = true;
        if (!fotos.current.has(t)) foraDeCampo.current = true; // sem foto, não há com o que comparar
      } else if (t && (t as HTMLElement).isContentEditable) {
        foraDeCampo.current = true;
      }
    };
    const aoClicar = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || ehCampoOuRotulo(t)) return;
      if (clicavel(t, corpo)) foraDeCampo.current = true;
    };
    const aoTeclar = (e: KeyboardEvent) => {
      fotografar();
      const t = e.target as Element | null;
      if (!t || !corpo.contains(t) || ehCampo(t)) return;
      if (!NAVEGACAO.has(e.key)) { foraDeCampo.current = true; return; }
      if (SETAS.has(e.key) && t.closest(WIDGET)) foraDeCampo.current = true;
    };
    const aoColar = (e: Event) => { if (!ehCampo(e.target)) foraDeCampo.current = true; };
    const aoArrastarOuSoltar = () => { foraDeCampo.current = true; };

    const noCorpo: Array<[string, EventListener]> = [
      ['focusin', fotografar], ['pointerdown', fotografar], ['mousedown', fotografar],
      ['input', aoMexer], ['change', aoMexer], ['click', aoClicar as EventListener],
      ['keydown', aoTeclar as EventListener], ['paste', aoColar], ['dragstart', aoArrastarOuSoltar],
    ];
    // O drop vale no modal INTEIRO: há telas cuja zona de soltar é o modal todo (Demandas), cabeçalho incluído.
    const noDialogo: Array<[string, EventListener]> = [['drop', aoArrastarOuSoltar]];
    noCorpo.forEach(([nome, fn]) => corpo.addEventListener(nome, fn, true));
    noDialogo.forEach(([nome, fn]) => dialogo.addEventListener(nome, fn, true));
    return () => {
      noCorpo.forEach(([nome, fn]) => corpo.removeEventListener(nome, fn, true));
      noDialogo.forEach(([nome, fn]) => dialogo.removeEventListener(nome, fn, true));
    };
  }, []);

  const mudou = () => {
    if (foraDeCampo.current) return true;
    if (!mexeuEmCampo.current) return false;
    let diferente = false;
    fotos.current.forEach((foto, el) => { if (el.isConnected && valorDe(el) !== foto) diferente = true; });
    return diferente;
  };

  return { corpoRef, mudou };
}
